<template>
  <section class="slider">
    <div class="slider__preload">
      <img
        v-for="(picture, i) in data.images"
        :key="`${picture.image}-${i}`"
        :src="picture.image"
        :alt="picture.caption"
      >
    </div>
    <div class="c-container">
      <div class="slider__container">
        <transition
          @enter="enter"
          @leave="leave"
        >
          <figure
            v-for="number in [currentNumber]"
            :key="number"
            :data-anim="isFirst ? 'picture' : false"
          >
            <img
              :data-src="isFirst ? currentPicture.image : false"
              :src="isFirst ? false : currentPicture.image"
              :alt="currentPicture.caption"
            />
            <div
              class="slider__image picture__image-bg"
              :style="{
                backgroundImage: isFirst ? null : `url(${currentPicture.image})`,
              }"
              :data-hover="currentPicture.alt"
            ></div>
          </figure>
        </transition>
      </div>
      <div class="slider__controls">
        <button @click="prev()">prev</button>
        <button @click="next()">Next</button>
      </div>
    </div>
  </section>
</template>

<script>
import TweenLite from 'gsap/TweenLite';
import { Power4 } from 'gsap/EasePack';

export default {
  name: 'Slider',

  props: {
    data: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      currentNumber: 0,
      isFirst: true,
      direction: null,
      isAnimated: false,
    };
  },

  computed: {
    currentPicture() {
      const currentPicture = this.data.images[
        Math.abs(this.currentNumber) % this.data.images.length
      ];
      return currentPicture;
    },
  },

  methods: {
    next() {
      if (this.isAnimated) return;

      this.isAnimated = true;
      if (this.isFirst) this.isFirst = false;

      this.currentNumber += 1;
      this.direction = 'next';
    },

    prev() {
      if (this.isAnimated) return;
      this.isAnimated = true;

      if (this.isFirst) this.isFirst = false;

      this.currentNumber -= 1;
      this.direction = 'prev';
    },

    enter(el, done) {
      const slide = el.querySelector('.slider__image');
      const clip = this.direction === 'prev' ? 'inset(0% 100% 0.01% 0%)' : 'inset(0% 0.01% 0% 100%)';
      TweenLite.set(slide, { autoAlpha: 1 });

      TweenLite.fromTo(slide, 1, {
        webkitClipPath: clip,
        clipPath: clip,
      }, {
        webkitClipPath: 'inset(0.01% 0.02% 0.03% 0.04%)',
        clipPath: 'inset(0.01% 0.02% 0.03% 0.04%)',
        clearProps: 'clipPath',
        delay: 0.2,
        ease: Power4.easeOut,
        onComplete: () => {
          this.isAnimated = false;
          done();
        },
      });

      TweenLite.from(slide, 0.9, { delay: 0.1, ease: Power4.easeOut });
    },

    leave(el, done) {
      const slide = el.querySelector('.slider__image');
      const clip = this.direction === 'next' ? 'inset(0% 100% 0% 0.01%)' : 'inset(0% 0.01% 0% 100%)';

      TweenLite.fromTo(slide, 0.8, {
        webkitClipPath: 'inset(0.01% 0.02% 0.03% 0.04%)',
        clipPath: 'inset(0.01% 0.02% 0.03% 0.04%)',
      }, {
        webkitClipPath: clip,
        clipPath: clip,
        clearProps: 'clipPath',
        ease: Power4.easeOut,
        onComplete: done,
      });

      TweenLite.to(slide, 0.7, { delay: 0.1, ease: Power4.easeOut });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~sass-mq/mq";
@import '../style/colors';
@import '../style/variables';
@import '../style/functions';
@import '../style/mixins';


.slider {
  &__preload,
  &__preload img {
    @include visually-hidden;
  }

  &__container {
    display: inline-block;
    position: relative;
    width: 100%;

    @include aspect-ratio(16, 9);

    @include mq(ipadP) {
      width: 80%;
    }

    figure {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      background-position: center center;
      background-size: cover;
      background-repeat: no-repeat;
    }

    img {
      @include visually-hidden;
    }

    .slider__image {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      background-position: center center;
      background-size: cover;
      background-repeat: no-repeat;

      z-index: 5;
      @include boost-performance;

      &::after {
        content: attr(data-hover);

        position: absolute;
        bottom: -40px;
        left: 20px;
        max-width: 350px;
      }

       @include mq($and: $is-not-touch-device) {
        &::after {
          opacity: 0;
          transition: opacity 0.3s ease-out;
        }

        &:hover::after {
          opacity: 1
        }
      }
    }
  }

  &__controls {
    display: inline-block;
    position: relative;
    margin-left: 40px;

    button {
      display: block;

      background-color: transparent;
      border: none;
      outline: transparent;

      text-transform: uppercase;
      font-size: font-size(m);
      font-weight: bold;

      cursor: pointer;

      &:last-of-type {
        font-size: font-size(xl);
        margin-bottom: 20px;
        color: transparent;
        -webkit-text-stroke: 1px $color-text;
      }
    }
  }
}
</style>
